<template>
  <div>
    <ts-page-title
      :title="$t('monthlyReportSummary.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('monthlyReportSummary.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper>
        <div class="row">
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label required ts-mb-2">{{
              $t('monthlyReportSummary.date')
            }}</label>
            <DatePicker
              v-model="model.date_range"
              type="daterange"
              placement="bottom-end"
              :placeholder="$t('selectDateRange')"
              style="width: 100%"
              :transfer="true"
              format="dd-MM-yyyy"
              @on-change="onChangeTransactionDate"
            ></DatePicker>
          </div>
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2">{{
              $t('monthlyReportSummary.cashier')
            }}</label>
            <Select
              v-model="model.user_id"
              :clearable="true"
              filterable
              placeholder="All"
              :loading="cashierLoading"
              :not-found-text="cashierLoading ? '' : 'No options available'"
            >
              <Option
                v-for="(cashier, index) in cashiers"
                :key="index"
                :value="cashier.value"
                >{{ cashier.label }}
              </Option>
            </Select>
          </div>
          <div class="col-md-4 form-group ts-px-2 ts-pt-6">
            <Button
              class="ts-mb-1"
              type="primary"
              :disabled="loading"
              :loading="loading"
              @click="fetchData"
              >{{ $t('view') }}
            </Button>
            <Button
              class="ts-mb-1 ts-ml-3"
              type="success"
              :disabled="exporting"
              :loading="exporting"
              @click.prevent="exportExcel"
              ghost
            >
              <Icon type="ios-document-outline" />
              {{ $t('excel') }}
            </Button>
          </div>
        </div>
      </ts-panel-wrapper>
      <div style="max-height: 75vh">
        <Table
          :columns="columns"
          :data="resources"
          size="small"
          :loading="loading"
          class="tw-rounded-md"
          max-height="700"
          :show-summary="true"
          :summary-method="handleSummary"
        >
          <template slot-scope="{ row }" slot="stock_out_date">
            <strong>
              {{ row.stock_out_date }}
            </strong>
          </template>
          <template slot-scope="{ row }" slot="number_invoice">
            <strong>
              {{ row.number_invoice }}
            </strong>
          </template>
          <template slot-scope="{ row }" slot="paid_usd">
            <strong>
              {{ formatPrice(row.paid_usd) }}
            </strong>
          </template>
          <template slot-scope="{ row }" slot="paid_in_riel">
            <strong>
              {{ formatPrice(row.paid_in_riel) }}
            </strong>
          </template>
          <template slot-scope="{ row }" slot="paid_in_usd">
            <strong>
              {{ formatPrice(row.paid_in_usd) }}
            </strong>
          </template>
          <template slot-scope="{ row }" slot="bank_transfer">
            <strong>
              {{ formatPrice(row.bank_transfer) }}
            </strong>
          </template>
          <template slot-scope="{ row }" slot="change_in_usd">
            <strong>
              {{ formatPrice(row.change_in_usd) }}
            </strong>
          </template>
          <template slot-scope="{ row }" slot="change_in_riel">
            <strong>
              {{ formatPrice(row.change_in_riel) }}
            </strong>
          </template>
        </Table>
      </div>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination v-model="pagination"></ts-i-pagination>
      </div>
    </ts-panel>
  </div>
</template>
<script>
import { Errors } from 'form-backend-validation'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import * as FileDownload from 'downloadjs'
import { trim, sumBy } from 'lodash'

export default {
  data () {
    return {
      exporting: false,
      loading: false,
      userLoading: false,
      errors: new Errors(),
      waiting: false,
      cashiers: [],
      cashierLoading: false,
      stockOutDate: [],
      fx_rate: 0,
      model: {
        user_id: '',
        date_range: [
          moment().format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY')
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['formatCurrencyWithCode']),
    ...mapState('report/finance', ['resources', 'pagination']),

    sumBy: vm => column => {
      const flatResource = Object.values(vm.resources).flat()
      return sumBy(flatResource, item => parseFloat(item[column]))
    },
    columns () {
      return [
        {
          title: this.$t('monthlyReportSummary.date'),
          slot: 'stock_out_date'
        },
        {
          title: this.$t('monthlyReportSummary.paidOfInvoice'),
          slot: 'number_invoice',
          key: 'number_invoice',
          align: 'center'
        },
        {
          title: this.$t('monthlyReportSummary.sale'),
          slot: 'paid_usd',
          key: 'paid_usd',
          align: 'center'
        },
        {
          title: this.$t('monthlyReportSummary.cashInKhr'),
          slot: 'paid_in_riel',
          key: 'paid_in_riel',
          align: 'center'
        },
        {
          title: this.$t('monthlyReportSummary.cashInUsd'),
          slot: 'paid_in_usd',
          key: 'paid_in_usd',
          align: 'center'
        },
        {
          title: this.$t('monthlyReportSummary.bankInUsd'),
          slot: 'bank_transfer',
          key: 'bank_transfer',
          align: 'center'
        },
        {
          title: this.$t('monthlyReportSummary.ChangeUsd'),
          slot: 'change_in_usd',
          key: 'change_in_usd',
          align: 'center'
        },
        {
          title: this.$t('monthlyReportSummary.ChangeKhr'),
          slot: 'change_in_riel',
          key: 'change_in_riel',
          align: 'center'
        }
      ]
    }
  },
  created () {
    this.fetchCashier()
  },
  methods: {
    formatPrice (value) {
      let val = (value / 1).toFixed(2).replace(',', '.')
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    async fetchCashier () {
      this.userLoading = true
      await this.$store
        .dispatch('report/finance/getCashier')
        .then(response => {
          this.cashiers = response.data.map(el => ({
            value: el.user_id,
            label: el.name
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.userLoading = false
        })
    },
    onChangeTransactionDate (date_range) {
      this.model.date_range = date_range[0] && date_range[1] ? date_range : []
    },

    fetchData () {
      this.loading = true
      this.error = new Errors()
      this.$store
        .dispatch('report/finance/fetch', this.model)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
    },
    exportExcel () {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/finance/summarySaleReportExportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    handleSummary ({ columns, data }) {
      const sums = {}
      columns.forEach((column, index) => {
        const key = column.key
        if (index === 0) {
          sums[key] = {
            key,
            value: this.$t('journalEntry.total')
          }
          return
        }
        const values = data.map(item => Number(item[key]))
        if (!values.every(value => isNaN(value))) {
          const v = values.reduce((prev, curr) => {
            const value = Number(curr)
            if (!isNaN(value)) {
              return prev + curr
            } else {
              return prev
            }
          }, 0)
          sums[key] = {
            key,
            value: this.formatCurrencyWithCode(v, this.currency)
          }
        } else {
          sums[key] = {
            key,
            value: 'N/A'
          }
        }
      })

      return sums
    },
    notice (not) {
      this.$Notice[not.type]({
        title: 'MONTHLY SALE REPORT',
        desc: not.text
      })
    }
  }
}
</script>
<style scoped>
.max-h {
  max-height: 70vh;
}
</style>
